<script setup lang="ts">
import EmotivoLogo from '@/assets/brands/emotivo_logo.svg?skipsvgo'
import ExpProLogo from '@/assets/brands/exp-pro_logo.svg?skipsvgo'
import FormulaDriveLogo from '@/assets/brands/fd_logo.svg?skipsvgo'
import PrezentMarzenLogo from '@/assets/brands/pm_logo.svg?skipsvgo'
import Poligon4x4Logo from '@/assets/brands/pologon4x4_logo.svg?skipsvgo'
import FunzeumLogo from '@/assets/brands/funzeum_logo.svg?skipsvgo'
import TutayLogo from '@/assets/brands/tutay_logo.svg?skipsvgo'

const route = useRoute()
const store = useStore()
const { footerMenu, shopInformation } = storeToRefs(store)
const phone = `tel:+48${shopInformation.value.shopInformation.phone.replaceAll(' ', '')}`
const mail = `mailto:${shopInformation.value.shopInformation.contactEmail}`
const year = new Date().getFullYear()
let mobile = ref<string[]>([])
const load = (index: string) => {
  if (mobile.value.includes(index)) {
      mobile.value.splice(mobile.value.indexOf(index), 1)
  } else {
      mobile.value.push(index)
  }
}
interface Link {
  extensions: {
    categoryUrlData: {
      linkType: string
      urlPrefix: string
      url: string
    }
  },
  externalLink: string
}
const loadNewsletter = computed(() => ['index', 'k-id', 'tor-id', 'b-slug', 'p-slug'].includes(route.name))
const getLink = ({ extensions: { categoryUrlData: { linkType, urlPrefix, url } }, externalLink }: Link) => {
  return linkType === 'external' ? externalLink : `/${urlPrefix}/${url}`
}
const getTargetLink = (target: boolean) => {
  return target ? '_blank' : null
}
</script>

<template>
  <div class="container mx-auto">
    <Newsletter v-if="loadNewsletter" />
  </div>
  <div class="mt-12 flex items-center justify-center bg-primary p-2.5 text-center text-xs text-white sm:text-base lg:h-[260px] xl:text-24">
    <div class="flex h-full w-full flex-col items-center pb-5">
      <span class="my-5 inline-flex items-center md:whitespace-nowrap lg:mb-10 lg:mt-10 text-10 sm:text-14 md:text-24">
        Jesteśmy częścią spółki
        <a href="https://www.emotivo.pl/" aria-label="Emotivo" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 sm:ml-2 ml-1">
          <EmotivoLogo class="block max-h-[30px] w-auto py-[5px] md:max-h-12 sm:p-0" />
        </a>
        , poznaj inne nasze marki:
      </span>
      <div class="flex flex-wrap items-center justify-center gap-3 lg:gap-2 xl:gap-3">
        <a href="https://www.exp-pro.pl/" aria-label="Exp-pro" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <ExpProLogo class="block max-h-8 w-auto py-[5px] sm:p-0" />
        </a>
        <span class="mx-2.5 hidden h-12 w-px bg-white/50 lg:inline-block xl:mx-5 2xl:mx-12" />
        <a href="https://www.formula-drive.pl/" aria-label="Formula-drive" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <FormulaDriveLogo class="block max-h-8 w-auto py-[5px] sm:p-0" />
        </a>
        <span class="mx-2.5 hidden h-12 w-px bg-white/50 lg:inline-block xl:mx-5 2xl:mx-12" />
        <a href="https://prezentmarzen.com/" aria-label="PrezentMarzen" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <PrezentMarzenLogo class="block max-h-8 w-auto py-[5px] sm:p-0" />
        </a>
        <span class="mx-2.5 hidden h-12 w-px bg-white/50 lg:inline-block xl:mx-5 2xl:mx-12" />
        <a href="https://www.poligon4x4.pl/" aria-label="Poligon4x4" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <Poligon4x4Logo class="block max-h-8 w-auto py-[5px] sm:p-0" />
        </a>
        <span class="mx-2.5 hidden h-12 w-px bg-white/50 lg:inline-block xl:mx-5 2xl:mx-12" />
        <a href="https://funzeum.pl/" aria-label="Funzeum" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <FunzeumLogo class="block max-h-12 w-auto py-[5px] sm:p-0" />
        </a>
        <span class="mx-2.5 hidden h-12 w-px bg-white/50 lg:inline-block xl:mx-5 2xl:mx-12" />
        <a href="https://tutay.pl/" aria-label="Tutay" rel="nofollow noopener noreferrer" target="_blank" class="inline-flex basis-[48%] transform items-center justify-center transition duration-200 ease-in-out hover:scale-105 lg:basis-auto">
          <TutayLogo class="block max-h-12 w-auto py-[5px] sm:p-0" />
        </a>
      </div>
    </div>
  </div>
  <div class="container mx-auto">
    <ul class="flex flex-col lg:flex-row justify-between my-[50px] border-gray-400">
      <li class="p-[10px] border-b border-solid border-gray-500 lg:border-none">
        <span class="text-30 lg:text-16 font-bold text-primary">Kontakt</span>
        <ul class="w-56 py-[10px] text-14">
          <li class="p-[5px]">
            <NuxtLink :to="phone" class="inline-flex">
              <i class="icon-phone w-[40px] text-[20px]" />
              <span class="flex flex-col">
                <span class="text-24">{{ shopInformation.shopInformation.phone }}</span>
                <span class="text-12">{{ shopInformation.shopInformation.workingHours }}</span>
              </span>
            </NuxtLink>
          </li>
          <li class="p-[5px]">
            <NuxtLink :to="mail" class="inline-flex">
              <i class="icon-mail w-[40px] text-[20px]" />
              <span class="flex flex-col">
                <span class="font-medium">{{ shopInformation.shopInformation.contactEmail }}</span>
              </span>
            </NuxtLink>
          </li>
        </ul>
        <ul class="flex gap-[20px] text-[20px] mt-[20px]">
          <li>
            <NuxtLink to="ttps://www.facebook.com/goracingpl" aria-label="Facebook" target="_blank">
              <i class="icon-fb" />
            </NuxtLink>
          </li>
          <li>
            <NuxtLink to="https://www.instagram.com/goracing.pl/" aria-label="Instagram" target="_blank">
              <i class="icon-instagram" />
            </NuxtLink>
          </li>
          <li>
            <NuxtLink to="https://www.youtube.com/channel/UCEdVDV_efJn6tpPWXefkCGQ" aria-label="Youtube" target="_blank">
              <i class="icon-youtube" />
            </NuxtLink>
          </li>
        </ul>
      </li>
      <li v-for="link_1 in footerMenu" :key="link_1.id" class="p-[10px] border-b border-solid border-gray-500 lg:border-none">
        <span class="flex justify-between items-center text-16 font-bold text-primary" @click="load(link_1.id)">
          <span>{{ link_1.name }}</span>
          <i v-if="mobile.includes(link_1.id)" class="icon-arrow-menu lg:hidden text-[15px] rotate-[-90deg]" />
          <i v-else class="icon-arrow-menu lg:hidden text-[15px] rotate-90" />
        </span>
        <ul v-if="link_1.children.length" v-show="mobile.includes(link_1.id)" class="w-56 py-[10px] lg:!block">
          <li v-for="link_2 in link_1.children" :key="link_2.id" class="p-[5px]">
            <NuxtLink :to="getLink(link_2)" :target="getTargetLink(link_2.extensions.categoryUrlData.linkNewTab)" class="inline-flex text-16">
              <span>{{ link_2.name }}</span>
            </NuxtLink>
          </li>
        </ul>
      </li>
    </ul>
    <div class="flex flex-col lg:flex-row items-center justify-between text-16 mb-[100px] lg:mb-[50px]">
      <p class="flex flex-col items-center mb-[15px] lg:mb-0">
        <small>2009-{{ year }} Copyright © {{ shopInformation.shopInformation.shopName }}</small>
        <small>Wszelkie prawa zastrzeżone.</small>
      </p>
      <PaymentMethods class="gap-[15px]" />
    </div>
  </div>
</template>
